import { Injectable, Injector, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { AuthCredentialLockService } from './auth-credential-lock.service';
import { AuthSessionStateService, type AuthSharedState } from './auth-session-state.service';
import { isPublicAuthFlowUrl } from '../utils/auth-endpoints.util';
import { AUTH_SESSION_UI_CLEANUP, type AuthSessionUiCleanup } from './auth-session-ui.token';

export type SessionEndReason =
  | 'refresh-rejected'
  | 'retry-401'
  | 'session-expired'
  | 'idle-timeout'
  | 'timer-refresh-rejected'
  | 'startup-rejected'
  | 'cross-tab'
  | 'user-logout';

export interface SessionEndOptions {
  /** Adds `sessionExpired=true` to the login URL, so the page can explain why. */
  sessionExpired?: boolean;
  /** Skip the server call — for an end that another tab already reported to the server. */
  notifyServer?: boolean;
}

const CHANNEL_NAME = 'marina-auth';

/**
 * The one way a session ends.
 *
 * Before this existed there were six: the error interceptor, two branches of the auth interceptor,
 * the refresh timer, the idle timer and the startup path each called `logout()` and navigated on
 * their own. Because the first of those ran per failed request, a dashboard that fired ten parallel
 * calls into a single expired session produced ten logout requests and ten navigations — which is
 * exactly what the incident console showed.
 *
 * Every entry point now comes here, and this is idempotent per session: the first accepted call wins
 * and the rest are logged and dropped.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionTerminatorService {
  private endingSessionId: string | null = null;
  private sessionEnded = false;
  private channel: BroadcastChannel | null = null;

  /**
   * The session id this tab most recently saw in shared state. Kept because the shared value is
   * gone by the time another tab's "session-ended" reaches us — the sender clears it before it
   * posts — so "was that our session?" has to be answered from memory.
   */
  private lastKnownSessionId: string | null = null;

  /** Supplied by AuthService to avoid a DI cycle (AuthService already depends on this service). */
  private clearLocalState: (() => void) | null = null;
  private notifyServerLogout: (() => Promise<void>) | null = null;

  constructor(
    private readonly router: Router,
    private readonly sessionState: AuthSessionStateService,
    private readonly lock: AuthCredentialLockService,
    /**
     * AUTH_SESSION_UI_CLEANUP is resolved only when a session actually ends. Looking it up here
     * would construct SignalR hubs (via the app factory) while AuthService is still being
     * created — hubs need ENSURE_FRESH_CREDENTIALS, which needs AuthService: NG0200, blank app.
     */
    private readonly injector: Injector,
  ) {
    this.sessionState.state$.subscribe((state) => this.onSharedStateChanged(state));
    this.sessionState.clearedElsewhere$.subscribe(() => this.onClearedElsewhere());
    this.listenForOtherTabs();
  }

  registerHandlers(clearLocalState: () => void, notifyServerLogout: () => Promise<void>): void {
    this.clearLocalState = clearLocalState;
    this.notifyServerLogout = notifyServerLogout;
  }

  isCurrent(sessionId: string | null | undefined): boolean {
    return this.sessionState.isCurrent(sessionId);
  }

  /** True once this session has begun ending, so callers can stop treating it as live. */
  isEnding(sessionId: string | null | undefined): boolean {
    return !!sessionId && this.endingSessionId === sessionId;
  }

  /**
   * True from the moment any session end is accepted until the next login.
   *
   * What it is for: once the session is over there is nothing to authenticate with, so continuing to
   * send protected requests only produces failures — each one a red line in the console and, on the
   * way, a pointless refresh attempt against cookies the server has already cleared.
   */
  isSessionOver(): boolean {
    return this.sessionEnded;
  }

  /**
   * End `sessionId`, once.
   *
   * Ignored unless `sessionId` is the login currently in the jar — a failure belonging to a login the
   * user has already replaced must not touch the new one.
   */
  end(reason: SessionEndReason, sessionId: string | null, options: SessionEndOptions = {}): void {
    // Once is enough, whatever the id. Judging idempotency by session id alone left a hole: a
    // session with no client identity (a login inherited from before identities existed) has a null
    // id, and every one of N parallel failures then passed the guard and ran the whole sequence —
    // N logout requests and N navigations, the storm this service exists to prevent.
    if (this.sessionEnded) {
      this.log(`[AuthSessionTerminator] Session already over (${reason}); ignoring`);
      return;
    }

    const targetId = sessionId ?? this.sessionState.sessionId ?? this.lastKnownSessionId;

    // A failure belonging to a login the user has since *replaced* must not touch the new one.
    // Replaced means the jar now names a different session. An empty jar is not a newer login — it
    // is this one already gone, cleared by another tab — and this tab still has to stop.
    const current = this.sessionState.sessionId;
    if (targetId && current && current !== targetId) {
      this.log(`[AuthSessionTerminator] Ignoring "${reason}" for a session that was replaced by a newer login`);
      return;
    }

    this.endingSessionId = targetId;
    this.sessionEnded = true;
    const notifyServer = options.notifyServer !== false;

    this.log(`[AuthSessionTerminator] Ending session (${reason}), notifyServer=${notifyServer}`);

    // Hubs first, while this tab still knows the session is over. SignalR negotiates on its own
    // HTTP client, so leaving a hub up after the cookies go produces the login-page 1006 / 401
    // storm. stopRealtime() is expected to flip its "enabled" flag synchronously.
    this.stopRealtime();

    // Local state first: the UI must stop behaving as if signed in immediately, whatever the network
    // does next.
    this.clearLocalState?.();
    this.sessionState.clear();
    this.closeOverlays();

    if (notifyServer) {
      this.broadcast(targetId, reason);
    }

    this.navigateToLogin(options.sessionExpired === true);

    if (notifyServer) {
      // Deliberately *not* awaited, and deliberately its own lock acquisition. The caller may be
      // holding the credential lock right now (a refresh that just failed), and Web Locks are not
      // re-entrant: taking it again from inside would deadlock. Queuing it means it runs once the
      // current holder releases.
      void this.notifyServerAfterRelease(targetId);
    }
  }

  /** Re-arm for the next login. Called when a new session starts. */
  reset(): void {
    this.endingSessionId = null;
    this.sessionEnded = false;
  }

  /**
   * Follow the shared state.
   *
   * A new session id appearing while this tab believes the session is over means a login completed —
   * here or in another tab. The gate has to open, or this tab could send nothing until it was
   * reloaded. (A null here is *not* acted on: it is also what an unreadable storage looks like.)
   */
  private onSharedStateChanged(state: AuthSharedState | null): void {
    if (!state?.sessionId) {
      return;
    }
    if (this.sessionEnded && state.sessionId !== this.endingSessionId) {
      this.log('[AuthSessionTerminator] A new session began; re-arming');
      this.reset();
    }
    this.lastKnownSessionId = state.sessionId;
  }

  /**
   * Another tab removed the shared state, which only ending a session does. This tab must end too,
   * whether or not that tab's broadcast ever arrives — and it must not tell the server again.
   */
  private onClearedElsewhere(): void {
    if (this.sessionEnded || !this.lastKnownSessionId) {
      return;
    }
    this.log('[AuthSessionTerminator] Shared session state was cleared by another tab');
    this.end('cross-tab', this.lastKnownSessionId, { notifyServer: false });
  }

  /**
   * Dismiss anything floating above the page, before navigating.
   *
   * Overlays are not children of the router outlet, so the navigation below leaves them exactly where
   * they were — a modal from the previous screen sitting on top of the login form.
   *
   * A full page reload would also clear them, and was considered. It is rejected because it unloads
   * the page while the `POST /auth/logout` below is still in flight, cancelling it and leaving the
   * session alive on the server until it expires on its own.
   */
  private closeOverlays(): void {
    for (const cleanup of this.uiCleanups()) {
      try {
        cleanup.closeOverlays();
      } catch (error) {
        // A failure to tidy the UI must never stop the session from ending.
        this.log('[AuthSessionTerminator] UI cleanup failed (ignored)', error);
      }
    }
  }

  private stopRealtime(): void {
    for (const cleanup of this.uiCleanups()) {
      try {
        cleanup.stopRealtime?.();
      } catch (error) {
        this.log('[AuthSessionTerminator] Realtime stop failed (ignored)', error);
      }
    }
  }

  private uiCleanups(): AuthSessionUiCleanup[] {
    return this.injector.get(AUTH_SESSION_UI_CLEANUP, []);
  }

  private async notifyServerAfterRelease(sessionId: string | null): Promise<void> {
    if (!this.notifyServerLogout) {
      return;
    }
    try {
      await this.lock.runExclusive('logout', async () => {
        // Re-check inside the lock: a login may have completed while this queued, and telling the
        // server to log out now would end *that* session instead of the one we meant.
        if (sessionId && this.sessionState.sessionId && this.sessionState.sessionId !== sessionId) {
          this.log('[AuthSessionTerminator] A newer login exists; skipping the server logout');
          return;
        }
        await this.notifyServerLogout!();
      });
    } catch (error) {
      // A logout we could not deliver changes nothing locally; the session is already over here.
      this.log('[AuthSessionTerminator] Server logout failed (ignored)', error);
    }
  }

  private navigateToLogin(sessionExpired: boolean): void {
    const currentUrl = this.router.url;

    if (isPublicAuthFlowUrl(currentUrl)) {
      this.log('[AuthSessionTerminator] On a public auth flow; not navigating');
      return;
    }

    const queryParams: Record<string, unknown> = { returnUrl: currentUrl };
    if (sessionExpired) {
      queryParams['sessionExpired'] = true;
    }

    void this.router.navigate(['/auth/login'], { queryParams });
  }

  private broadcast(sessionId: string | null, reason: SessionEndReason): void {
    try {
      this.channel ??= typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
      // The reason travels with the message. Without it the receiving tab can only report that
      // "another tab ended this session", which is true, useless, and the reason a spurious logout
      // on dev could not be traced from the console of the tab it happened in.
      this.channel?.postMessage({ type: 'session-ended', sessionId, reason });
    } catch {
      // Broadcast is a convenience: other tabs still discover the end on their next request.
    }
  }

  private listenForOtherTabs(): void {
    try {
      if (typeof BroadcastChannel === 'undefined') {
        return;
      }
      this.channel ??= new BroadcastChannel(CHANNEL_NAME);
      this.channel.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as
          { type?: string; sessionId?: string | null; reason?: SessionEndReason } | null;
        if (data?.type !== 'session-ended') {
          return;
        }
        // Only if it is *our* session: another tab ending a different login is not our business.
        // Judged against what this tab last knew, not against shared state — the sender has already
        // cleared that by the time the message arrives, so a shared-state check would always say no.
        const endedId = data.sessionId ?? null;
        const why = data.reason ?? 'unspecified';

        if (endedId && this.lastKnownSessionId && endedId !== this.lastKnownSessionId) {
          this.log(
            `[AuthSessionTerminator] Ignoring "${why}" from another tab: it ended ${endedId}, ` +
            `we hold ${this.lastKnownSessionId}`);
          return;
        }

        if (!endedId && this.lastKnownSessionId) {
          // The sender had no identity of its own, so it cannot say whether it meant this login.
          // Acting on it anyway is deliberate -- auth cookies are per browser profile, so a session
          // that ended in one tab has ended for all of them -- but it is the one case where a tab is
          // signed out on someone else's word, so it is called out rather than logged as routine.
          this.log(
            `[AuthSessionTerminator] Another tab ended an unidentified session (${why}); ` +
            `treating ${this.lastKnownSessionId} as ended, since cookies are shared`);
        } else {
          this.log(`[AuthSessionTerminator] Another tab ended this session (${why})`);
        }

        // notifyServer: false — the tab that ended it already told the server.
        this.end('cross-tab', endedId ?? this.lastKnownSessionId, { notifyServer: false });
      });
    } catch {
      // No BroadcastChannel: cross-tab discovery falls back to the next request's 401.
    }
  }

  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }
}
